import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../components/UI/Button";
import Card from "../components/UI/Card";

const AboutScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const locationFeatures = [
    {
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      title: t("about.location.sidiAbdellah"),
      description: t("about.location.fromMarrakech"),
    },
    {
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      title: t("about.location.fromAirport"),
      description: t("about.location.fromCenter"),
    },
    {
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
      title: t("about.location.property"),
      description: t("about.location.fromAtlas"),
    },
  ];

  return (
    <div className="min-h-screen  ">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-33.jpg')`,
          }}
        ></div>

        {/* Floating Elements */}
        <div className="absolute top-16 left-8 w-16 h-16 bg-white/10 rounded-full animate-float"></div>
        <div
          className="absolute bottom-20 right-12 w-12 h-12 bg-accent/30 rounded-full animate-float"
          style={{ animationDelay: "1s" }}
        ></div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-sofia font-bold mb-6 animate-fade-in-up">
            {t("about.title")}
          </h1>
          <p
            className="text-xl md:text-2xl font-sofia font-light animate-fade-in-up opacity-90"
            style={{ animationDelay: "0.2s" }}
          >
            {t("hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Main About Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="animate-slide-in-left">
              <h2 className="text-4xl font-sofia font-bold text-black mb-8">
                {t("about.title")}
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg font-sofia text-text-secondary leading-relaxed mb-6">
                  {t("about.description")}
                </p>
              </div>

              <div className="flex gap-4 mt-8">
                <Button
                  onClick={() => navigate("/our-villas")}
                  className="hover:scale-105 transform transition-all duration-300"
                >
                  {t("nav.ourVillas")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/booking")}
                  className="hover:scale-105 transform transition-all duration-300"
                >
                  {t("common.bookNow")}
                </Button>
              </div>
            </div>

            <div className="animate-slide-in-right">
              <div className="relative">
                <img
                  src="/assets/images/villa Baidane-01.jpg"
                  alt="Village Baidane"
                  className="w-full h-96 object-cover rounded-2xl shadow-large"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>

          {/* Location Features */}
          <div className="mb-20">
            <div className="text-center mb-16 animate-fade-in-up">
              <h2 className="text-4xl font-sofia font-bold text-black mb-6">
                {t("about.location.title")}
              </h2>
              <p className="text-lg font-sofia text-text-secondary max-w-3xl mx-auto">
                {t("hero.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {locationFeatures.map((feature, index) => (
                <Card
                  key={index}
                  hover
                  className="text-center group animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 bg-[#81736d] rounded-full flex items-center justify-center mx-auto mb-4 text-white group-hover:bg-accent group-hover:text-white transition-all duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-sofia font-semibold text-[#81736d] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary font-sofia">
                    {feature.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>

          {/* Target Audience Section */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-large animate-fade-in-up">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-sofia font-bold text-black mb-6">
                  {t("about.targetAudience.title")}
                </h2>
                <p className="text-lg font-sofia text-text-secondary leading-relaxed">
                  {t("about.targetAudience.description")}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  {/* <div className="text-4xl mb-2">👨‍👩‍👧‍👦</div> */}
                  <div className="font-sofia font-semibold text-[#81736d]">
                    {t("about.audience.families.title")}
                  </div>
                  <div className="font-sofia text-sm text-text-light">
                    {t("about.audience.families.description")}
                  </div>
                </div>
                <div className="text-center">
                  {/* <div className="text-4xl mb-2">💑</div> */}
                  <div className="font-sofia font-semibold text-[#81736d]">
                    {t("about.audience.couples.title")}
                  </div>
                  <div className="font-sofia text-sm text-text-light">
                    {t("about.audience.couples.description")}
                  </div>
                </div>
                <div className="text-center">
                  {/* <div className="text-4xl mb-2">🧘‍♀️</div> */}
                  <div className="font-sofia font-semibold text-[#81736d]">
                    {t("about.audience.wellness.title")}
                  </div>
                  <div className="font-sofia text-sm text-text-light">
                    {t("about.audience.wellness.description")}
                  </div>
                </div>
                <div className="text-center">
                  {/* <div className="text-4xl mb-2">🏛️</div> */}
                  <div className="font-sofia font-semibold text-[#81736d]">
                    {t("about.audience.culture.title")}
                  </div>
                  <div className="font-sofia text-sm text-text-light">
                    {t("about.audience.culture.description")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-30.jpg')`,
          }}
        ></div>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 "></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="animate-fade-in-up">
            <h2 className="text-3xl font-sofia font-bold mb-6">
              {t("about.vision.title")}
            </h2>
            <p className="text-lg font-sofia font-light mb-8 opacity-90">
              {t("about.vision.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#81736d] hover:bg-[#81736d]"
                onClick={() => navigate("/booking")}
              >
                {t("common.bookNow")}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-[#81736d] hover:text-white"
                onClick={() => navigate("/contact")}
              >
                {t("common.contactUs")}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutScreen;
